import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { vehicleService } from '@/services/vehicleService'
import { employeesService } from '@/services/employeesService'
import type { VehicleVm, VehicleTypeOption } from '@/api/types/vehicle.types'
import { showError, showSuccess } from '@/lib/swal'

function toDateInputValue(d?: string): string {
  if (!d) return ''
  try {
    const date = new Date(d)
    return date.toISOString().slice(0, 10)
  } catch {
    return String(d).slice(0, 10)
  }
}

function toIsoOrUndefined(d?: string): string | undefined {
  if (!d) return undefined
  const value = String(d).trim()
  if (!value) return undefined
  // Input type="date" gives "YYYY-MM-DD"; Swagger sample uses ISO datetime.
  const isoCandidate = value.includes('T') ? value : `${value}T00:00:00.000Z`
  const dt = new Date(isoCandidate)
  if (Number.isNaN(dt.getTime())) return undefined
  return dt.toISOString()
}

export const VehicleEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const isEditing = Boolean(id && id !== 'new')

  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<{ id?: number; empCode?: string; empFirstNameEn?: string; empSecondNameEn?: string; empLastNameEn?: string }[]>([])
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeOption[]>([])

  const [form, setForm] = useState({
    empId: 0,
    vehicleCompanyName: '',
    modelYear: '' as string | number,
    vehicleColor: '',
    vehicleNumber: '',
    vehicleTypeId: 0,
    parkingAllotted: false,
    parkingAllotNumber: '' as string | number,
    registrationStartDate: '',
    registrationEndDate: '',
  })

  useEffect(() => {
    if (!branchId) return
    const loadEmployees = async () => {
      try {
        const res = await employeesService.getAllEmployees(branchId, 1, 500)
        const data = (res as any)?.data ?? (res as any)?.Data ?? res
        setEmployees(Array.isArray(data) ? data : [])
      } catch {
        setEmployees([])
      }
    }
    loadEmployees()
  }, [branchId])

  useEffect(() => {
    vehicleService.getVehicleTypes().then((data) => setVehicleTypes(data ?? [])).catch(() => setVehicleTypes([]))
  }, [])

  useEffect(() => {
    if (!isEditing || !id) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await vehicleService.getById(Number(id))
        const raw = data as any
        const isParkingAllot = raw?.isParkingAllot === true || raw?.isParkingAllot === 1
        const parkingAllotted =
          isParkingAllot || String(raw?.isParkingAllot ?? raw?.parkingAllotted ?? '').toUpperCase() === 'YES'
        setForm({
          empId: data.empId ?? 0,
          vehicleCompanyName: data.vehicleCompanyName ?? '',
          modelYear: data.modelYear ?? data.model ?? 0,
          vehicleColor: data.modelColor ?? data.vehicleColor ?? '',
          vehicleNumber: data.carNumberPlate ?? data.carNumber ?? data.vehicleNumber ?? '',
          vehicleTypeId: data.carTypeId ?? data.vehicleTypeId ?? 0,
          parkingAllotted,
          parkingAllotNumber: data.parkingAllotNumber ?? '',
          registrationStartDate: toDateInputValue(data.carRegistrationStartDate ?? data.registrationStartDate),
          registrationEndDate: toDateInputValue(data.carRegistrationEndDate ?? data.registrationEndDate),
        })
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? 'Failed to load vehicle.')
        navigate('/vehicles')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditing, id, navigate])

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => {
        const fullName = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()
        const label = fullName ? `${e.empCode ?? ''} — ${fullName}`.trim() : e.empCode ?? `Emp ${e.id}`
        return { value: String(e.id ?? 0), label }
      }).filter((o) => o.value !== '0'),
    [employees]
  )

  const vehicleTypeOptions = useMemo(
    () =>
      vehicleTypes.map((t) => {
        const name = (t as any).lookupNameEn ?? (t as any).lookupNameAr ?? (t as any).name ?? `Type ${t.id}`
        return { value: String(t.id ?? 0), label: name }
      }),
    [vehicleTypes]
  )

  const handleChange = (patch: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...patch }))
  }

  const handleSave = async () => {
    if (!branchId) {
      toast.error('Please select a branch.')
      return
    }
    if (!form.empId || form.empId === 0) {
      toast.error('Please select an employee.')
      return
    }
    if (!form.vehicleCompanyName.trim()) {
      toast.error('Vehicle Company Name is required.')
      return
    }
    const modelYear = typeof form.modelYear === 'number' ? form.modelYear : parseInt(String(form.modelYear), 10)
    if (!Number.isFinite(modelYear) || modelYear < 1900 || modelYear > 2100) {
      toast.error('Model Year is required and must be valid (e.g. 2020).')
      return
    }
    if (!form.vehicleColor.trim()) {
      toast.error('Vehicle Color is required.')
      return
    }
    if (!form.vehicleNumber.trim()) {
      toast.error('Vehicle Number is required.')
      return
    }
    const allotNum = typeof form.parkingAllotNumber === 'number' ? form.parkingAllotNumber : parseInt(String(form.parkingAllotNumber), 10)
    if (!Number.isFinite(allotNum) || allotNum < 0) {
      toast.error('Parking Allot Number must be 0 or greater.')
      return
    }

    const carRegistrationStartDate = toIsoOrUndefined(form.registrationStartDate)
    const carRegistrationEndDate = toIsoOrUndefined(form.registrationEndDate)

    const payload: VehicleVm = {
      // Swagger sample sends id: 0 for new
      id: isEditing && id ? Number(id) : 0,
      empId: form.empId,
      // Backend (Swagger) field names
      vehicleCompanyName: form.vehicleCompanyName.trim(),
      modelId: 0,
      modelYear,
      modelColor: form.vehicleColor.trim(),
      carNumberPlate: form.vehicleNumber.trim(),
      carTypeId: form.vehicleTypeId,
      isParkingAllot: form.parkingAllotted ? 1 : 0,
      parkingAllotNumber: allotNum,

      carRegistrationStartDate,
      carRegistrationEndDate,
      branchId,

      // Frontend/backward compatibility field names
      vehicleColor: form.vehicleColor.trim(),
      vehicleNumber: form.vehicleNumber.trim(),
      carNumber: form.vehicleNumber.trim(),
      vehicleTypeId: form.vehicleTypeId,
      parkingAllotted: form.parkingAllotted,
      registrationStartDate: form.registrationStartDate || undefined,
      registrationEndDate: form.registrationEndDate || undefined,
    }

    try {
      setLoading(true)
      if (isEditing) {
        await vehicleService.update(payload)
        await showSuccess('Updated', 'Vehicle updated successfully.')
      } else {
        await vehicleService.save(payload)
        await showSuccess('Saved', 'Vehicle saved successfully.')
      }
      navigate('/vehicles')
    } catch (e: any) {
      await showError('Save failed', e?.response?.data?.message ?? e?.message ?? 'Failed to save vehicle.')
    } finally {
      setLoading(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Vehicle" description="Add or edit vehicle." />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">Please select a branch first.</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Vehicle"
        description={isEditing ? 'Edit vehicle' : 'Add new vehicle'}
      />

      <Card>
        <CardHeader>
          <CardTitle>Vehicle</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <SearchableSelect
                options={[{ value: '0', label: 'Select employee' }, ...employeeOptions]}
                value={form.empId ? String(form.empId) : '0'}
                onValueChange={(v) => handleChange({ empId: Number(v) || 0 })}
                placeholder="Select employee"
                searchPlaceholder="Search employee..."
                emptyMessage="No employee found"
                disabled={isEditing}
              />
            </div>
            <div className="space-y-2">
              <Label>Vehicle Company Name *</Label>
              <Input
                value={form.vehicleCompanyName}
                onChange={(e) => handleChange({ vehicleCompanyName: e.target.value })}
                placeholder="Vehicle company name"
              />
            </div>
            <div className="space-y-2">
              <Label>Model Year *</Label>
              <Input
                type="number"
                min={1900}
                max={2100}
                value={form.modelYear === '' ? '' : form.modelYear}
                onChange={(e) => handleChange({ modelYear: e.target.value ? parseInt(e.target.value, 10) : '' })}
                placeholder="Model"
              />
            </div>
            <div className="space-y-2">
              <Label>Vehicle Color *</Label>
              <Input
                value={form.vehicleColor}
                onChange={(e) => handleChange({ vehicleColor: e.target.value })}
                placeholder="Vehicle color"
              />
            </div>
            <div className="space-y-2">
              <Label>Vehicle Number *</Label>
              <Input
                value={form.vehicleNumber}
                onChange={(e) => handleChange({ vehicleNumber: e.target.value })}
                placeholder="Vehicle number"
              />
            </div>
            <div className="space-y-2">
              <Label>Vehicle Type</Label>
              <SearchableSelect
                options={[{ value: '0', label: 'Select vehicle type' }, ...vehicleTypeOptions]}
                value={form.vehicleTypeId ? String(form.vehicleTypeId) : '0'}
                onValueChange={(v) => handleChange({ vehicleTypeId: Number(v) || 0 })}
                placeholder="Select vehicle type"
                searchPlaceholder="Search..."
                emptyMessage="No vehicle type found"
              />
            </div>
            <div className="space-y-2">
              <Label>Parking Allotted *</Label>
              <Select
                value={form.parkingAllotted ? 'YES' : 'NO'}
                onValueChange={(v) => handleChange({ parkingAllotted: v === 'YES' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="YES">YES</SelectItem>
                  <SelectItem value="NO">NO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Parking Allot Number *</Label>
              <Input
                type="number"
                min={0}
                value={form.parkingAllotNumber === '' ? '' : form.parkingAllotNumber}
                onChange={(e) => handleChange({ parkingAllotNumber: e.target.value ? parseInt(e.target.value, 10) : '' })}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label>Vehicle Registration Start Date</Label>
              <Input
                type="date"
                value={form.registrationStartDate}
                onChange={(e) => handleChange({ registrationStartDate: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Vehicle Registration End Date</Label>
              <Input
                type="date"
                value={form.registrationEndDate}
                onChange={(e) => handleChange({ registrationEndDate: e.target.value })}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => navigate('/vehicles')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return
            </Button>
            <Button type="button" onClick={handleSave} disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
